import {
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useEffect, useState } from "react";
import { UserManagement } from "src/interfaces";
import { SubjectType } from "src/interfaces/KnowledgeBase";
import { adminService } from "src/services/services/admin/admin.api";
import { knowledgeBaseService } from "src/services/services/knowledgeBase/knowledgeBase.api";
import { updateSubjectsSchema } from "src/validations";

interface Props {
  opened: boolean;
  onClose: () => void;
  user?: UserManagement | null;
  onUpdate: (subjectIds: number[]) => Promise<void> | void;
}

const UpdateSubjectsModal = ({ opened, onClose, user, onUpdate }: Props) => {
  const [availableSubjects, setAvailableSubjects] = useState<SubjectType[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    initialValues: { subjectIds: [] as number[] },
    validate: yupResolver(updateSubjectsSchema),
  });

  useEffect(() => {
    if (!opened) return;
    setError(null);
    const fetchSubjects = async () => {
      setLoading(true);
      try {
        let levelId = 1;

        if (user?.level) {
          // Try to parse numeric levels first
          const parsed = parseInt(String(user.level), 10);
          if (!Number.isNaN(parsed) && parsed > 0) {
            levelId = parsed;
          } else {
            // Fallback: fetch known levels and try to map by name
            const levelsResp = await knowledgeBaseService.getLevels();
            if (levelsResp.kind === "ok" && levelsResp.data) {
              const match = levelsResp.data.find((l) => {
                const name = String(l.name).toLowerCase();
                const userLevel = String(user.level).toLowerCase();
                return (
                  l.id === Number(user.level) ||
                  name === userLevel ||
                  name.includes(userLevel) ||
                  userLevel.includes(name)
                );
              });

              if (match) levelId = match.id;
            }
          }
        }

        const response = await knowledgeBaseService.getSubjects(levelId);
        let list: SubjectType[] = [];
        if (response.kind === "ok" && response.data) {
          list = response.data;
        }

        setAvailableSubjects(list);

        // Pre-select subjects based on api call
        let preselectedIds: number[] = [];

        if (user?.username) {
          const childSubjectsResp = await adminService.getChildSubjects(user.username);
          if (childSubjectsResp.kind === "ok" && childSubjectsResp.data) {
            // The API returns the subjects that SHOULD be selected.
            // We need to match them with the available subjects (list) by ID.
            const childSubjectIds = childSubjectsResp.data.map((s) => s.id);
            preselectedIds = list
              .filter(s => childSubjectIds.includes(s.id))
              .map(s => s.id);
          }
        }

        // internal fallback (existing logic) if api didn't return anything or failed, 
        // essentially if preselectedIds is empty, we MIGHT want to fall back to user.subjects
        // BUT the user request says: "extract each items subject and id, then from all of those shown as a result of the api call of subjects based on level, u have to show these ones as selected"
        // indicating the API is the source of truth for pre-selection.

        // If the API returns nothing, maybe we shouldn't pre-select anything? 
        // Or should we fallback to `user.subjects` string array matching?
        // The user said: "kind of we will be prefilling users subjects."
        // I will prefer the API result. If it's empty, then empty.

        // However, if the API call fails or user.username is missing, we might want to keep robust behavior?
        // The prompt implies this API call is the way to go.

        // Let's also keep the old logic as a fallback if `childSubjectsResp` wasn't successful/called?
        // Actually, let's prioritize the new API.

        if (preselectedIds.length === 0 && user?.subjects) {
          // Fallback to existing string matching if new API didn't result in selection (optional, but safer)
          const userSubjects = user?.subjects || [];
          const stringMatchedIds = list
            .filter((subject) => userSubjects.includes(subject.name))
            .map((subject) => subject.id);
          if (stringMatchedIds.length > 0) preselectedIds = stringMatchedIds;
        }

        // Ensure required subjects are selected
        const requiredSubjectIds = list.filter((s) => s.is_required).map((s) => s.id);
        const uniqueIds = new Set([...preselectedIds, ...requiredSubjectIds]);
        preselectedIds = Array.from(uniqueIds);

        form.setFieldValue("subjectIds", preselectedIds);
      } catch (e) {
        console.error(e);
        setError("Could not load subjects");
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, [opened, user]);


  const toggle = (id: number) => {
    const subject = availableSubjects.find((s) => s.id === id);
    if (subject?.is_required) return;

    const cur = new Set(form.values.subjectIds || []);
    if (cur.has(id)) cur.delete(id);
    else cur.add(id);
    form.setFieldValue("subjectIds", Array.from(cur));
    setError(null);
  };

  const handleUpdate = form.onSubmit(async (values) => {
    if (!user) return;

    setActionLoading(true);
    try {
      await onUpdate(values.subjectIds || []);
      onClose();
    } catch (e) {
      setError("Could not update subjects");
    } finally {
      setActionLoading(false);
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Update subjects`}
      centered
      zIndex={2100}
    >
      <Stack spacing="sm">
        <Text size="sm" color="dimmed">
          Select one or more subjects for the user
        </Text>

        {loading ? (
          <Group position="center">
            <Loader />
          </Group>
        ) : (
          <div className="flex flex-wrap gap-2">
            {availableSubjects.map((subject) => (
              <Checkbox
                key={subject.id}
                label={subject.name}
                checked={(form.values.subjectIds || []).includes(subject.id)}
                onChange={() => toggle(subject.id)}
                size="sm"
                disabled={subject.is_required}
              />
            ))}
            {availableSubjects.length === 0 && (
              <Text size="sm" color="dimmed">
                No subjects available
              </Text>
            )}
          </div>
        )}

        {form.values.subjectIds.length === 0 && (
          <Text size="xs" className="text-yellow-700">
            Please select at least one subject to continue
          </Text>
        )}

        {error && (
          <Text className="text-red-500" size="sm">
            {error}
          </Text>
        )}
        {form.errors.subjectIds && (
          <Text color="red" size="sm">
            {String(form.errors.subjectIds)}
          </Text>
        )}

        <Group position="right" mt="md">
          <Button
            variant="default"
            onClick={() => {
              form.reset();
              onClose();
            }}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            color="dark"
            onClick={() => handleUpdate()}
            loading={actionLoading}
            // atleast one subject should be slected
            disabled={form.values.subjectIds.length === 0}
          >
            Update
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default UpdateSubjectsModal;
